import classNames from "classnames";
import { Spinner } from "common/components/Spinner";
import React, { useCallback, useEffect, useRef, useState } from "react";
import gfm from "remark-gfm";
import styles from "./Markdown.module.css";

const ReactMarkdown = React.lazy(
  () => import(/* webpackChunkName: "react-markdown" */ "react-markdown"),
);

/*
Based on problem with resizing images using react-markdown,
https://jacobwicks.github.io/2020/06/19/rendering-markdown-and-resizing-images-with-react-markdown.html
*/
export const useElementResize = (
  ref: React.RefObject<HTMLDivElement>,
): number | undefined => {
  const getWidth = useCallback(() => ref.current?.offsetWidth, [ref]);

  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const handleResize = () => {
      setWidth(getWidth());
    };

    if (ref.current) {
      setWidth(getWidth());
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [ref, getWidth]);

  return width;
};

interface Props {
  content: string;
  style?: string | string[];
  openLinksSeparately?: boolean;
}

const ExternalLinkRenderer = ({
  href,
  children,
}: React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>) => {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const ImageRenderer = ({
  src,
  maxWidth,
  alt,
}: React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & { maxWidth?: number }) => {
  // TODO remove width={600} when react-markdown supports image resizing and add
  // =x700 in the markdownEditors image option
  return <img style={{ maxWidth: maxWidth }} src={src} alt={alt} width={700} />;
};

const TableRenderer = (
  props: React.DetailedHTMLProps<
    React.TableHTMLAttributes<HTMLTableElement>,
    HTMLTableElement
  > & { maxWidth?: number },
) => {
  return <table {...props} className={styles.markdownTable} />;
};

export const Markdown: React.FC<React.PropsWithChildren<Props>> = ({
  content,
  style,
  openLinksSeparately,
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const maxWidth = useElementResize(divRef);

  return (
    <div ref={divRef} style={{}}>
      <React.Suspense fallback={<Spinner />}>
        <ReactMarkdown
          className={classNames(style)}
          components={{
            ...(openLinksSeparately && { a: ExternalLinkRenderer }),
            img(props) {
              return <ImageRenderer maxWidth={maxWidth} {...props} />;
            },
            table(props) {
              return <TableRenderer {...props} />;
            },
          }}
          remarkPlugins={[gfm]}
        >
          {content}
        </ReactMarkdown>
      </React.Suspense>
    </div>
  );
};
